import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useDeviceCommand, useReportCommandFailure } from '../../hooks/useDeviceCommand';
import { useSwitchState } from '../../hooks/useSwitchState';
import { useTemperature } from '../../hooks/useTemperature';
import { freezeDevice } from '../../services';
import { inputMessage } from '../controlBox/inputMessages';
import { SWITCH_SLICES } from '../store/slices/switchSlices';
import { selectUserInfo, selectUserPermissions } from '../store/slices/userSlice';

// Instant heat accepts these temperatures, in the operator's unit.
const INSTANT_HEAT_RANGE = { C: [121, 999], F: [250, 1830] };

/**
 * One device's card state and buttons for ESS, TGS or TES (`system`),
 * shared by the desktop and mobile layouts.
 *
 * Locks: a read-only operator (no WRITE) changes nothing; the program
 * buttons (instant heat, snow sensor, fan) also lock while the device is
 * off or disabled, or while EBP mode 0 is on for a device with an EBP.
 */
export const useMachineCard = ({ system, location, machine }) => {
  const actions = SWITCH_SLICES[system];
  const { flat } = useSwitchState(system);
  const device = flat[location][machine];
  const { deviceMac, isOff, instantHeat, snowSensor, fanOnly, openMachineController } = device;

  const dispatch = useDispatch();
  const { user } = useSelector(selectUserInfo);
  const permissions = useSelector(selectUserPermissions);
  const { isF, unit, toDisplay, fromInput } = useTemperature();
  const sendCommand = useDeviceCommand(system, deviceMac, device.device_name);
  const reportFailure = useReportCommandFailure(system, deviceMac, device.device_name);
  const at = { location, machine };

  const readOnly = !permissions.WRITE;
  const ebpLocked = device.EBP_mode === 0 && Boolean(device.isEbp);
  const programsLocked = isOff || device.isDisabled || readOnly || ebpLocked;

  // The stored °C temperature in the operator unit, or empty when there is none.
  const storedInstantTemp =
    instantHeat.inputTemp > 0 ? `${toDisplay(instantHeat.inputTemp)} ${unit}` : '';
  const [instantTemp, setInstantTemp] = useState('');
  const [message, setMessage] = useState(null);

  useEffect(() => {
    setInstantTemp(storedInstantTemp);
  }, [storedInstantTemp]);

  const editInstantTemp = (text) => {
    if (!programsLocked) setInstantTemp(text);
  };

  const toggleShutOff = () => {
    if (readOnly) return;
    freezeDevice(system, !isOff, deviceMac, user.user_id)
      .then(() => dispatch(actions.shutOff(at)))
      .catch(reportFailure);
  };

  const toggleSnowSensor = () => {
    if (programsLocked) return;
    if (snowSensor.isReady) {
      sendCommand({ snow_enabled: 0 });
      dispatch(actions.snowSensorOff(at));
    } else {
      sendCommand({ snow_enabled: 1 });
      dispatch(actions.snowSensorOn(at));
    }
  };

  const toggleFan = () => {
    if (programsLocked) return;
    dispatch(actions.fanOnly({ ...at, state: !fanOnly }));
    sendCommand({ fan: fanOnly ? 0 : 1 });
  };

  // The form's submit and its button both land here; the button's click is
  // prevented from also submitting the form.
  const submitInstantHeat = (event) => {
    event.preventDefault();
    if (programsLocked) return;
    if (instantHeat.isActivated || instantHeat.isReady) {
      sendCommand({ on_switch: 0 });
      dispatch(actions.instantHeatOff(at));
      return;
    }
    const typed = instantTemp.match(/\d+/);
    const temp = typed ? Number(typed[0]) : NaN;
    const [min, max] = INSTANT_HEAT_RANGE[isF ? 'F' : 'C'];
    if (temp >= min && temp <= max) {
      const celsius = fromInput(temp);
      sendCommand({ on_switch: 1, instant_temp: celsius });
      dispatch(actions.instantHeatOn({ ...at, temp: celsius }));
    } else {
      setMessage(inputMessage('tempA', 'instant heat'));
    }
  };

  const closeMessage = () => {
    setInstantTemp(storedInstantTemp);
    setMessage(null);
  };

  const openController = (status) => dispatch(actions.openController({ ...at, status }));

  // Desktop: expand opens the device's controls; close shuts them and the
  // SSR panel.
  const expand = () => {
    if (!isOff) openController(true);
    sessionStorage.setItem('machineId', machine);
  };

  const close = () => {
    openController(false);
    if (actions.expandSSRDetail) {
      dispatch(actions.expandSSRDetail({ ...at, status: false }));
    }
    sessionStorage.removeItem('machineId');
  };

  // Mobile: one button toggles the controls; closing clears the picked program.
  const toggleMobileControls = () => {
    if (isOff) return;
    if (openMachineController) {
      openController(false);
      dispatch(actions.unselectPrograms(at));
    } else {
      openController(true);
    }
  };

  return {
    device,
    readOnly,
    programsLocked,
    instantTemp,
    editInstantTemp,
    message,
    closeMessage,
    toggleShutOff,
    toggleSnowSensor,
    toggleFan,
    submitInstantHeat,
    expand,
    close,
    toggleMobileControls,
  };
};
